import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vesselService, locationService } from '../services/api';
import { showToast } from '../components/Toaster';
import FavoriteButton from '../components/FavoriteButton';
import MapExplorer from '../components/MapExplorer';
import LatestCatches from '../components/LatestCatches';

function HomePage() {
  const [activeTab, setActiveTab] = useState<'vessel' | 'location'>('vessel');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hoveredResultIdx, setHoveredResultIdx] = useState<number | null>(null);
  const [daysBack, setDaysBack] = useState(30);
  const navigate = useNavigate();

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setHasSearched(true);
    try {
      if (activeTab === 'vessel') {
        const results = await vesselService.search(searchQuery);
        setSearchResults(results);
      } else {
        const results = await locationService.search(searchQuery);
        setSearchResults(results);
      }
    } catch (error) {
      showToast('Feil ved søk. Vennligst prøv igjen.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectResult = (result: any) => {
    if (activeTab === 'vessel') {
      const vesselId = result.registreringsmerke || result.vessel_name;
      navigate(`/vessel/${vesselId}`, { state: result });
    } else {
      navigate(`/location/${result.location_id}`, { state: result });
    }
  };

  return (
    <div className="card" style={{ maxWidth: '800px', margin: '10px auto 40px auto' }}>
      <div style={{ marginBottom: '30px', display: 'flex', gap: '10px' }}>
        <button
          onClick={() => {
            setActiveTab('vessel');
            setSearchResults([]);
            setSearchQuery('');
            setHasSearched(false);
          }}
          className={`btn-${activeTab === 'vessel' ? 'primary' : 'secondary'}`}
          style={{ flex: 1 }}
        >
          Fartøysøk
        </button>
        <button
          onClick={() => {
            setActiveTab('location');
            setSearchResults([]);
            setSearchQuery('');
            setHasSearched(false);
          }}
          className={`btn-${activeTab === 'location' ? 'primary' : 'secondary'}`}
          style={{ flex: 1 }}
        >
          Stedssøk
        </button>
      </div>

      <form onSubmit={handleSearch} className="search-form">
        <div className="form-group">
          <label htmlFor="search">
            {activeTab === 'vessel' ? 'Søk på fartøynavn eller registreringsnummer' : 'Søk kommune eller fylke'}
          </label>
          <input
            id="search"
            type="text"
            placeholder={
              activeTab === 'vessel'
                ? 'f.eks. "Junior" eller registreringsnummer'
                : 'f.eks. "Bergen" eller "Vestland"'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Søker...' : 'Søk'}
        </button>
      </form>

      {searchResults.length > 0 && (
        <div style={{ marginTop: '30px' }}>
          <h3 style={{ marginBottom: '15px' }}>
            {searchResults.length} resultat{searchResults.length !== 1 ? 'er' : ''}
          </h3>
          <div style={{ display: 'grid', gap: '10px' }}>
            {searchResults.map((result, idx) => (
              <div
                key={idx}
                onClick={() => handleSelectResult(result)}
                onMouseEnter={() => setHoveredResultIdx(idx)}
                onMouseLeave={() => setHoveredResultIdx(null)}
                style={{
                  padding: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: hoveredResultIdx === idx ? '#f0f0f0' : '#f9f9f9',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {activeTab === 'vessel' ? (
                    <>
                      <strong>{result.vessel_display}</strong>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>
                        ({result.registreringsmerke})
                      </span>
                    </>
                  ) : (
                    <>
                      <strong>{result.location_name}</strong>
                      <span style={{ color: '#999', fontSize: '0.9rem' }}>
                        ({result.type || 'location'})
                      </span>
                    </>
                  )}
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  {activeTab === 'vessel' ? (
                    <FavoriteButton
                      type="vessel"
                      id={result.registreringsmerke || result.vessel_name}
                      registreringsmerke={result.registreringsmerke || result.vessel_name}
                      name={result.vessel_display || result.vessel_name}
                      size="small"
                    />
                  ) : (
                    <FavoriteButton
                      type="location"
                      id={result.location_id}
                      locationName={result.location_name}
                      locationType={result.type || 'location'}
                      size="small"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {searchResults.length === 0 && !loading && hasSearched && (
        <div className="error">
          Ingen resultater funnet. Prøv et annet søkeord.
        </div>
      )}

      <LatestCatches />

      <div style={{ marginTop: '20px', fontSize: '0.85rem', color: '#666', textAlign: 'center' }}>
        Kilde rådata: Fiskeridirektoratet
      </div>

      <MapExplorer
        daysBack={daysBack}
        onDaysBackChange={setDaysBack}
      />
    </div>
  );
}

export default HomePage;
